import { Iconify } from '@sohanemon/utils/components';
import { CodeBlock, CodeBlockFrame } from '@sohanscript/lib/code/code-block';
import { CopyButton } from '@sohanscript/ui/copy-button';
import { type ReactNode } from 'react';
import { resolveCodeBlockMeta } from './lib/resolve-code-block-meta';
import { MermaidBlock } from './mermaid-block';

export type CodeBlockProps = {
	className?: string;
	children?: ReactNode;
	'data-filename'?: string;
	'data-language'?: string;
	'data-icon'?: string;
	'data-highlight'?: string;
	'data-name'?: string;
	inline?: boolean;
	showLineNumbers?: boolean;
};

type PreProps = React.HTMLAttributes<HTMLPreElement> &
	CodeBlockProps & {
		hideHeader?: boolean;
		/** Language reported by the markdown parser (TanStack Markdown). */
		'data-lang'?: string;
		/** Raw fence meta, e.g. `filename="app.tsx" icon="mdi:react"`. */
		'data-meta'?: string;
		/** Parsed `title=` / `file=` fence meta (TanStack Markdown). */
		'data-code-title'?: string;
	};

export function Pre({
	children,
	hideHeader,
	'data-lang': lang,
	'data-meta': meta,
	'data-code-title': codeTitle,
	'data-filename': filename,
	'data-icon': icon,
	'data-name': name,
	'data-language': childLanguage,
	// NOTE: dropped, the parser's `tm-code` marker class has no styles here and
	// would land on our own wrapper instead of the `<pre>`.
	className: _className,
	...rest
}: PreProps) {
	const child = children as React.ReactElement<CodeBlockProps>;
	const metadata = resolveCodeBlockMeta({
		childProps: child?.props ?? {},
		childLanguage,
		dataLang: lang,
		dataMeta: meta,
		dataCodeTitle: codeTitle,
		dataFilename: filename,
		dataIcon: icon,
		dataName: name,
	});
	const {
		cleanCode,
		code,
		filename: resolvedFilename,
		highlight,
		icon: resolvedIcon,
		language,
		name: resolvedName,
	} = metadata;
	if (language === 'mermaid') return <MermaidBlock code={code} />;

	// NOTE: fenced metadata is forwarded so `parseFileEntries` can still read it
	// off the rendered `code` element inside editor/tabs directives.
	const codeProps = {
		'data-language': language,
		'data-highlight': highlight,
		'data-filename': resolvedFilename,
		'data-icon': resolvedIcon,
		'data-name': resolvedName,
	};

	if (hideHeader) {
		return (
			<CodeBlock
				code={code}
				lang={language}
				highlight={highlight}
				{...rest}
				{...codeProps}
			/>
		);
	}

	return (
		<CodeBlockFrame
			className="my-4"
			header={
				<>
					{resolvedIcon && (
						<Iconify icon={resolvedIcon} className="size-3.5 text-primary/60" />
					)}
					{resolvedFilename && (
						<span className="text-primary/60 text-xs">{resolvedFilename}</span>
					)}
					{language && !resolvedFilename && (
						<span className="text-primary/60 text-xs">{language}</span>
					)}
				</>
			}
			actions={<CopyButton className="ml-auto mr-2" text={cleanCode} />}
		>
			<CodeBlock
				code={code}
				lang={language}
				highlight={highlight}
				{...rest}
				{...codeProps}
			/>
		</CodeBlockFrame>
	);
}
